import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { AlertType, BannerType } from "_shared/_views/system-notification-banner/system-notification-banner";

export interface SystemBanner { id?: string; message: string; type: BannerType; }
export interface SystemNotificationBannerContextProps {
  banners: SystemBanner[];
  message: string | null;
  type: BannerType | null;
  setBanner: (type: AlertType | null, message: string) => void;
  clearBanner: () => void;
  setSystemBanners: (banners: SystemBanner[]) => void;
  dismissSystemBanner: (id: string) => void;
}
const Context = createContext<SystemNotificationBannerContextProps | undefined>(undefined);
let globalSetBanner: SystemNotificationBannerContextProps["setBanner"] | null = null;
export function getGlobalSystemNotificationBannerSetter() { return globalSetBanner; }
export function setSystemNotificationBanner(type: AlertType | null, message: string) {
  if (globalSetBanner) globalSetBanner(type, message);
}
export function clearSystemNotificationBanner() { if (globalSetBanner) globalSetBanner(null, ""); }

export const SystemNotificationBannerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [banners, setBanners] = useState<SystemBanner[]>([]);
  const setBanner = useCallback((type: AlertType | null, message: string) => {
    setBanners((current) => {
      const systemBanners = current.filter((banner) => banner.id);
      return type ? [...systemBanners, { message, type }] : systemBanners;
    });
  }, []);
  const clearBanner = useCallback(() => setBanners((current) => current.filter((banner) => banner.id)), []);
  const setSystemBanners = useCallback((next: SystemBanner[]) => setBanners((current) => [...current.filter((banner) => !banner.id), ...next]), []);
  const dismissSystemBanner = useCallback((id: string) => setBanners((current) => current.filter((banner) => banner.id !== id)), []);
  useEffect(() => { globalSetBanner = setBanner; return () => { if (globalSetBanner === setBanner) globalSetBanner = null; }; }, [setBanner]);
  const outage = banners.find((banner) => !banner.id);
  const contextValue = React.useMemo(() => ({ banners, message: outage?.message ?? null, type: outage?.type ?? null, setBanner, clearBanner, setSystemBanners, dismissSystemBanner }), [banners, outage, setBanner, clearBanner, setSystemBanners, dismissSystemBanner]);
  return <Context.Provider value={contextValue}>{children}</Context.Provider>;
};
export function useSystemNotificationBanner() {
  const ctx = useContext(Context);
  if (!ctx) throw new Error("useSystemNotificationBanner must be used within a SystemNotificationBannerProvider");
  return ctx;
}
